/**
 * Biometrics & Security Service for MyLife App Lock
 * Supports WebAuthn Platform Authenticator (Fingerprint, Touch ID, Face Unlock)
 * and SHA-256 PIN hashing via Web Crypto API.
 */

export async function isBiometricsAvailable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return false;
  }
  try {
    if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Register local device biometric credential (Fingerprint / Platform Authenticator)
 */
export async function registerBiometrics(username: string): Promise<{ success: boolean; credentialId?: string; error?: string }> {
  try {
    if (!window.PublicKeyCredential) {
      return { success: false, error: 'WebAuthn is not supported in this browser' };
    }

    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    const userId = new TextEncoder().encode(username || 'mylife-user');

    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'MyLife App',
          id: window.location.hostname || 'localhost',
        },
        user: {
          id: userId,
          name: username || 'user@mylife.app',
          displayName: 'MyLife User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
      },
    })) as PublicKeyCredential | null;

    if (!credential) {
      return { success: false, error: 'Failed to create biometric credential.' };
    }

    // Convert rawId to base64 string for persistent storage
    const rawIdBytes = new Uint8Array(credential.rawId);
    let binary = '';
    for (let i = 0; i < rawIdBytes.byteLength; i++) {
      binary += String.fromCharCode(rawIdBytes[i]);
    }
    const credentialId = btoa(binary);

    return { success: true, credentialId };
  } catch (err: any) {
    console.warn('Biometric registration error:', err);
    if (err.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric enrollment was cancelled or timed out.' };
    }
    return { success: false, error: err.message || 'Failed to register biometrics on this device.' };
  }
}

/**
 * Authenticate using local device biometrics (Fingerprint / Face ID)
 */
export async function authenticateBiometrics(credentialId?: string | null): Promise<{ success: boolean; error?: string }> {
  try {
    if (!window.PublicKeyCredential) {
      return { success: false, error: 'WebAuthn is not supported.' };
    }

    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    let allowCredentials: PublicKeyCredentialDescriptor[] | undefined = undefined;

    if (credentialId) {
      try {
        const binary = atob(credentialId);
        const rawId = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          rawId[i] = binary.charCodeAt(i);
        }
        allowCredentials = [
          {
            id: rawId.buffer,
            type: 'public-key',
            transports: ['internal'],
          },
        ];
      } catch {
        // Fallback to discoverable credential if parsing fails
      }
    }

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname || 'localhost',
        allowCredentials,
        userVerification: 'required',
        timeout: 60000,
      },
    });

    if (assertion) {
      return { success: true };
    }
    return { success: false, error: 'Biometric verification failed.' };
  } catch (err: any) {
    console.warn('Biometric authentication error:', err);
    if (err.name === 'NotAllowedError') {
      return { success: false, error: 'Biometric scan was cancelled or not recognized.' };
    }
    return { success: false, error: err.message || 'Biometric verification failed.' };
  }
}

/**
 * Securely hashes any string/password using SHA-256 with application salt
 */
export async function hashString(str: string, salt = '_mylife_auth_salt_2026'): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str + salt);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Securely hashes the PIN using SHA-256 with application salt
 */
export async function hashPin(pin: string): Promise<string> {
  return hashString(pin, '_mylife_app_lock_salt_2026');
}

/**
 * Verifies a PIN against the stored SHA-256 hash
 */
export async function verifyPin(pin: string, storedHash: string): Promise<boolean> {
  if (!storedHash || !pin) return false;
  const computedHash = await hashPin(pin);
  return computedHash === storedHash;
}

/**
 * Verifies a password against the stored SHA-256 hash
 */
export async function verifyPassword(password: string, storedHash: string): Promise<boolean> {
  if (!storedHash || !password) return false;
  const computedHash = await hashString(password);
  return computedHash === storedHash;
}

